// CustomerAgreementModal.jsx - Fixed with dynamic content based on order type
import React, { useState, useRef, useEffect } from 'react';
import { getCurrentTimestamp } from '../../utils/dateUtils';

const CustomerAgreementModal = ({ 
  isOpen, 
  onClose, 
  onSign, 
  customerInfo, 
  orderData,
  formatCurrency 
}) => {
  const [signature, setSignature] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  // Determine if this is a New Sale Wig order
  const isNewSaleWig = orderData?.orderType === 'New Sale Wig';

  // Generate dynamic content based on order type
  const getDocumentContent = () => {
    if (isNewSaleWig) {
      // NEW SALE WIG SIMPLIFIED AGREEMENT
      return `
        <style>
          .agreement-content * {
            color: #333 !important;
            background-color: transparent !important;
          }
          .sale-agreement {
            background-color: #fff8e1;
            border: 2px solid #ff9800;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
          }
          .field-row {
            margin: 10px 0;
            padding: 5px 0;
            border-bottom: 1px dotted #ccc;
          }
          .warranty-notice {
            background-color: #ffebee;
            border: 1px solid #f44336;
            border-radius: 4px;
            padding: 10px;
            margin: 15px 0;
          }
        </style>
        <div class="agreement-content">
          <div class="sale-agreement">
            <h2 style="color: #e65100; text-align: center; margin-bottom: 20px;">
              DINI WIGS SALE PURCHASE AGREEMENT
            </h2>
            
            <div class="field-row">
              <strong>Date:</strong> ${new Date().toLocaleDateString()}
            </div>
            
            <p style="margin: 15px 0; font-size: 14px;">
              Thank you for your purchase from the Dini Wigs Sale.
            </p>
            
            <div class="warranty-notice">
              <p><strong>** Wigs purchased at the sale do not include our standard warranty, and all repairs, color, and cut all have an additional cost. Please ask a sales representative for information on alterations costs.</strong></p>
            </div>
            
            <div class="field-row">
              <strong>Name:</strong> ${customerInfo?.customerName || 'N/A'}
            </div>
            <div class="field-row">
              <strong>Phone Number:</strong> ${customerInfo?.phone || 'N/A'}
            </div>
            <div class="field-row">
              <strong>Wig Code:</strong> ${customerInfo?.wigBarCode || orderData?.wigBarCode || 'N/A'}
            </div>
            <div class="field-row">
              <strong>Length:</strong> ${customerInfo?.wigLength || orderData?.wigLength || 'N/A'}
            </div>
            <div class="field-row">
              <strong>Price:</strong> $${formatCurrency ? formatCurrency(orderData?.totalAmount || 0) : (orderData?.totalAmount || 0).toFixed(2)}
            </div>
            
            <div class="warranty-notice">
              <p><strong>**All sales are final and non-refundable/exchangeable.**</strong></p>
              <p><strong>**All net fit alterations costs are determined at the drop-off appointment for the repair and vary between $250-$550 in price.**</strong></p>
            </div>
            
            <p style="margin: 20px 0; font-size: 14px;">
              I <strong>${customerInfo?.customerName || 'Customer'}</strong> agree to the terms above.
            </p>
          </div>
        </div>
      `;
    } else {
      // STANDARD NEW WIG AGREEMENT (abbreviated for modal)
      return `
        <style>
          .agreement-content * {
            color: #333 !important;
            background-color: transparent !important;
          }
          .important-notice {
            background-color: #fff3cd;
            border: 2px solid #ffc107;
            border-radius: 6px;
            padding: 12px;
            margin: 15px 0;
          }
          .field-row {
            margin: 8px 0;
            padding: 4px 0;
          }
        </style>
        <div class="agreement-content">
          <h2 style="color: #007bff; text-align: center; border-bottom: 2px solid #007bff; padding-bottom: 10px;">
            DINI WIGS PURCHASE AGREEMENT
          </h2>
          
          <p><em>This is the complete Dini Wigs purchase agreement including warranty terms, payment policy, and all standard protections.</em></p>
          
          <div class="important-notice">
            <h4>⚠️ IMPORTANT NOTICE!</h4>
            <ul>
              <li>ALL PURCHASES OF HAIR PRODUCTS ARE FINAL SALE!</li>
              <li>ALL CREDIT CARD PAYMENTS ARE SUBJECT TO AN ADDITIONAL 3% CHARGE</li>
              <li>DINI WIGS HAIR PRODUCTS ARE NON-RETURNABLE AND NON-REFUNDABLE</li>
              <li>ANY ITEM NOT PICKED UP WITHIN ONE (1) YEAR WILL BE FORFEITED</li>
            </ul>
          </div>
          
          <h3>Order Details:</h3>
          <div class="field-row"><strong>Customer:</strong> ${customerInfo?.customerName || 'N/A'}</div>
          <div class="field-row"><strong>Order #:</strong> ${orderData?.orderNo || 'N/A'}</div>
          <div class="field-row"><strong>Order Type:</strong> ${orderData?.orderType || 'N/A'}</div>
          <div class="field-row"><strong>Total:</strong> $${formatCurrency ? formatCurrency(orderData?.totalAmount || 0) : (orderData?.totalAmount || 0).toFixed(2)}</div>
          
          <h3>Payment Policy</h3>
          <p>Initial consultation fee ($250) is non-refundable and applied to wig purchase. New wig orders require a $2,500 deposit which is non-refundable.</p>
          
          <h3>Warranty Coverage</h3>
          <p>Dini Wigs guarantees workmanship for 12 months post deposit or 9 months post delivery (whichever is longer) against normal wear and tear.</p>
          
          <h3>Return Policy</h3>
          <p><strong>As of April 1, 2020, Dini Wigs will not be offering returns of any hair-containing products. THERE ARE NO EXCEPTIONS.</strong></p>
          
          <p style="font-size: 12px; font-style: italic; margin-top: 20px; padding: 15px; background-color: #e8f4f8; border-radius: 4px;">
            This agreement includes our complete terms including payment policy, warranty coverage, 
            wig cut policy, and all customer protections. The complete agreement will be saved 
            to Google Drive upon signing.
          </p>
        </div>
      `;
    }
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    isDrawingRef.current = true;
    setIsDrawing(true);
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    
    setSignature('signature_drawn');
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature('');
  };

  const handleSign = async () => {
    if (!signature) {
      alert('Please provide your signature before proceeding.');
      return;
    }

    setIsProcessing(true);
    
    try {
      const canvas = canvasRef.current;
      const signatureDataURL = canvas.toDataURL('image/png');
      
      const agreementData = {
        signature: signatureDataURL,
        signedAt: getCurrentTimestamp(),
        orderType: customerInfo?.orderType,
        customerName: customerInfo?.customerName,
        orderNo: orderData?.orderNo,
        totalAmount: orderData?.totalAmount,
        documentVersion: 'v2.24.25',
        signatureMethod: 'digital_canvas',
        termsAccepted: true,
        finalSaleAccepted: true
      };

      await onSign(agreementData);
      clearSignature();
      onClose();
    } catch (error) {
      console.error('Error signing agreement:', error);
      alert('Error signing agreement. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyles}>
      <div style={modalStyles}>
        {/* Header */}
        <div style={headerStyles}>
          <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>
            {isNewSaleWig ? 'Sale Purchase Agreement' : 'Purchase Agreement'} - {customerInfo?.orderType}
          </h2>
          <button 
            onClick={onClose}
            style={closeButtonStyles}
            disabled={isProcessing}
          >
            ×
          </button>
        </div>

        {/* Order Summary - Compact */}
        <div style={summaryStyles}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <strong>Order #{orderData?.orderNo}</strong> - {customerInfo?.customerName}
            </div>
            <div>
              <strong>{customerInfo?.orderType}</strong> - ${formatCurrency ? formatCurrency(orderData?.totalAmount || 0) : (orderData?.totalAmount || 0).toFixed(2)}
            </div>
            <div style={{ fontSize: '14px', color: '#333' }}>
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Document Content - Large Scrollable Area */}
        <div style={documentContainerStyles}>
          <h3 style={{ color: '#333', margin: '0 0 15px 0', fontSize: '18px', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>
            Agreement Terms and Conditions
          </h3>
          
          <div 
            style={documentScrollStyles}
            dangerouslySetInnerHTML={{ __html: getDocumentContent() }}
          />
        </div>

        {/* Signature Section */}
        <div style={signatureSectionStyles}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h4 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
              Digital Signature Required *
            </h4>
            <button
              type="button"
              onClick={clearSignature}
              style={clearButtonStyles}
              disabled={!signature}
            >
              Clear Signature
            </button>
          </div>
          
          <div style={signatureContainerStyles}>
            <canvas
              ref={canvasRef}
              width={600}
              height={120}
              style={canvasStyles}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => {
                e.preventDefault();
                startDrawing(e);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                draw(e);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                stopDrawing();
              }}
            />
            <div style={signatureInstructionsStyles}>
              {signature ? 
                '✓ Signature captured - Ready to sign agreement' : 
                'Please sign above using your mouse or finger (touch devices)'
              }
            </div>
          </div>
          
          <p style={helpTextStyles}>
            By providing your digital signature above, you acknowledge that you have read and agree to all terms and conditions in the purchase agreement.
          </p>
        </div>

        {/* Action Buttons */}
        <div style={buttonGroupStyles}>
          <button
            onClick={onClose}
            style={cancelButtonStyles}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={!signature || isProcessing}
            style={{
              ...signButtonStyles,
              backgroundColor: signature && !isProcessing ? '#28a745' : '#6c757d',
              cursor: signature && !isProcessing ? 'pointer' : 'not-allowed'
            }}
          >
            {isProcessing ? 'Processing Agreement...' : 'Sign Agreement & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Keep all your existing styles (same as before)
const overlayStyles = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalStyles = {
  backgroundColor: 'white',
  borderRadius: '12px',
  width: '98vw',
  height: '98vh',
  maxWidth: '1400px',
  maxHeight: '1200px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden'
};

const headerStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 25px',
  borderBottom: '2px solid rgb(126, 184, 243)',
  backgroundColor: '#f8f9fa',
  flexShrink: 0
};

const closeButtonStyles = {
  background: 'none',
  border: 'none',
  fontSize: '28px',
  cursor: 'pointer',
  color: '#666',
  padding: '0',
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  transition: 'background-color 0.2s'
};

const summaryStyles = {
  backgroundColor: '#f8f9fa',
  padding: '15px 25px',
  borderBottom: '1px solid #dee2e6',
  flexShrink: 0
};

const documentContainerStyles = {
  flex: 1,
  padding: '20px 25px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const documentScrollStyles = {
  flex: 1,
  border: '2px solid #dee2e6',
  borderRadius: '8px',
  padding: '30px',
  overflowY: 'auto',
  backgroundColor: '#ffffff !important',
  fontSize: '15px',
  lineHeight: '1.8',
  color: '#333 !important',
  minHeight: '500px'
};

const signatureSectionStyles = {
  padding: '20px 25px',
  borderTop: '2px solid #e9ecef',
  backgroundColor: '#f8f9fa',
  flexShrink: 0
};

const signatureContainerStyles = {
  border: '2px solid #007bff',
  borderRadius: '8px',
  backgroundColor: 'white',
  padding: '15px',
  textAlign: 'center'
};

const canvasStyles = {
  border: '1px dashed #ccc',
  borderRadius: '6px',
  cursor: 'crosshair',
  backgroundColor: 'white',
  display: 'block',
  margin: '0 auto',
  maxWidth: '100%',
  height: 'auto'
};

const signatureInstructionsStyles = {
  marginTop: '10px',
  fontSize: '14px',
  color: '#666',
  fontStyle: 'italic'
};

const clearButtonStyles = {
  padding: '8px 15px',
  border: '1px solid #dc3545',
  backgroundColor: 'transparent',
  color: '#dc3545',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  transition: 'all 0.2s'
};

const helpTextStyles = {
  fontSize: '12px',
  color: '#666',
  marginTop: '15px',
  margin: '15px 0 0 0',
  textAlign: 'center',
  fontStyle: 'italic'
};

const buttonGroupStyles = {
  display: 'flex',
  gap: '15px',
  justifyContent: 'flex-end',
  padding: '20px 25px',
  borderTop: '1px solid #e9ecef',
  backgroundColor: '#f8f9fa',
  flexShrink: 0
};

const cancelButtonStyles = {
  padding: '12px 24px',
  border: '2px solid #6c757d',
  backgroundColor: 'transparent',
  color: '#6c757d',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: '500',
  transition: 'all 0.2s'
};

const signButtonStyles = {
  padding: '12px 24px',
  border: 'none',
  color: 'white',
  borderRadius: '6px',
  fontSize: '16px',
  fontWeight: '500',
  transition: 'all 0.2s'
};

export default CustomerAgreementModal;